import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Building2, 
  Search,
  Star,
  MessageSquare,
  Crown,
  Loader2,
  Link2,
  Link2Off,
  Save,
  RefreshCw,
  ExternalLink,
  MapPin,
  Settings2,
  X,
  Check,
  Copy,
  List
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOrganizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingOrg, setEditingOrg] = useState(null);
  const [googleLocationId, setGoogleLocationId] = useState("");
  const [googleLocationName, setGoogleLocationName] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  
  // Google locations modal
  const [showLocationsModal, setShowLocationsModal] = useState(false);
  const [googleLocations, setGoogleLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/admin/organizations`);
      setOrganizations(response.data);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      toast.error("Erreur lors du chargement des établissements");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (org) => {
    setEditingOrg(org);
    setGoogleLocationId(org.google_location_id || "");
    setGoogleLocationName(org.google_location_name || "");
  };

  const closeEditModal = () => {
    setEditingOrg(null);
    setGoogleLocationId("");
    setGoogleLocationName("");
  };

  const handleSaveGoogleLocation = async () => {
    if (!editingOrg) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/organizations/${editingOrg.id}/google-location`, {
        google_location_id: googleLocationId,
        google_location_name: googleLocationName
      });
      
      // Update local state
      setOrganizations(orgs => orgs.map(org => 
        org.id === editingOrg.id 
          ? { ...org, google_location_id: googleLocationId, google_location_name: googleLocationName }
          : org
      ));
      
      toast.success("Configuration Google sauvegardée !");
      closeEditModal();
    } catch (error) {
      console.error("Error saving Google location:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncReviews = async (orgId) => {
    setSyncing(orgId);
    try {
      const response = await axios.post(`${API}/admin/organizations/${orgId}/sync-reviews`);
      toast.success(`${response.data.imported_count} avis importés !`);
      await fetchOrganizations();
    } catch (error) {
      console.error("Error syncing reviews:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la synchronisation");
    } finally {
      setSyncing(null);
    }
  };

  const fetchGoogleLocations = async () => {
    setLoadingLocations(true);
    setShowLocationsModal(true);
    try {
      const response = await axios.get(`${API}/admin/google-locations`);
      setGoogleLocations(response.data.locations || []);
      if (response.data.locations?.length === 0) {
        toast.info("Aucun établissement trouvé. Vérifiez que vous êtes Manager des établissements clients.");
      }
    } catch (error) {
      console.error("Error fetching Google locations:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du chargement des établissements Google");
    } finally {
      setLoadingLocations(false);
    }
  };

  const selectLocationForOrg = (location) => {
    setGoogleLocationId(location.name);
    setGoogleLocationName(location.title || location.locationName || "Établissement");
    setShowLocationsModal(false);
    toast.success("Location ID copié ! Sauvegardez pour appliquer.");
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.business_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié !");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-organizations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Établissements
          </h1>
          <p className="text-slate-500 mt-1">
            {organizations.length} établissements enregistrés
          </p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 rounded-xl"
            data-testid="search-organizations"
          />
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-blue-800 mb-2">Configuration manuelle Google Business</h3>
            <p className="text-sm text-blue-700">
              Le client doit ajouter <strong>responsia.ap@gmail.com</strong> comme Manager sur son Google Business.
            </p>
          </div>
          <Button
            onClick={fetchGoogleLocations}
            className="bg-blue-600 hover:bg-blue-700 rounded-xl whitespace-nowrap"
          >
            <List className="w-4 h-4 mr-2" />
            Voir mes établissements Google
          </Button>
        </div>
      </div>

      {/* Organizations Grid */}
      {filteredOrganizations.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrganizations.map((org) => (
            <div 
              key={org.id} 
              className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-all"
              data-testid={`org-card-${org.id}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{org.name}</h3>
                    <p className="text-sm text-slate-500 capitalize">{org.business_type}</p>
                  </div>
                </div>
                {org.subscription_plan === "premium" ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-full text-xs font-medium">
                    <Crown className="w-3 h-3" /> Premium
                  </span>
                ) : org.subscription_plan === "essential" ? (
                  <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    Essentiel
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">
                    Aucun plan
                  </span>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-amber-500 mb-1">
                    <Star className="w-4 h-4 fill-amber-500" />
                    <span className="font-bold">{org.google_rating?.toFixed(1) || "N/A"}</span>
                  </div>
                  <p className="text-xs text-slate-500">Note Google</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-slate-700 mb-1">
                    <MessageSquare className="w-4 h-4" />
                    <span className="font-bold">{org.total_reviews || 0}</span>
                  </div>
                  <p className="text-xs text-slate-500">Avis</p>
                </div>
              </div>

              {/* Google Business Status */}
              <div className="mb-4">
                {org.google_location_id ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">Google Business connecté</span>
                    </div>
                    <p className="text-xs text-emerald-600 truncate" title={org.google_location_id}>
                      {org.google_location_name || org.google_location_id}
                    </p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <Link2Off className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">Google non configuré</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(org)}
                  className="flex-1 rounded-xl"
                >
                  <Settings2 className="w-4 h-4 mr-1" />
                  Configurer Google
                </Button>
                {org.google_location_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSyncReviews(org.id)}
                    disabled={syncing === org.id}
                    className="rounded-xl"
                  >
                    {syncing === org.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>

              {/* Footer Info */}
              <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                Créé le {formatDate(org.created_at)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Aucun établissement trouvé</p>
        </div>
      )}

      {/* Edit Modal */}
      {editingOrg && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                Configurer Google Business
              </h2>
              <button 
                onClick={closeEditModal}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-slate-50 rounded-xl">
              <p className="font-medium text-slate-900">{editingOrg.name}</p>
              <p className="text-sm text-slate-500 capitalize">{editingOrg.business_type}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="location_id" className="text-slate-700">
                  Google Location ID *
                </Label>
                <Input
                  id="location_id"
                  type="text"
                  placeholder="accounts/123456789/locations/987654321"
                  value={googleLocationId}
                  onChange={(e) => setGoogleLocationId(e.target.value)}
                  className="mt-1 h-11 rounded-xl"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Format: accounts/XXXXX/locations/XXXXX
                </p>
              </div>

              <div>
                <Label htmlFor="location_name" className="text-slate-700">
                  Nom de l'établissement Google
                </Label>
                <Input
                  id="location_name"
                  type="text"
                  placeholder="Nom affiché sur Google"
                  value={googleLocationName}
                  onChange={(e) => setGoogleLocationName(e.target.value)}
                  className="mt-1 h-11 rounded-xl"
                />
              </div>

              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  Comment obtenir le Location ID ?
                </p>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Le client ajoute responsia.ap@gmail.com comme Manager</li>
                  <li>Connectez-vous à l'API Google Business avec votre compte</li>
                  <li>Listez les locations pour trouver l'ID</li>
                </ol>
                <a 
                  href="https://developers.google.com/my-business/content/basic-api-operations" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                >
                  <ExternalLink className="w-3 h-3" />
                  Documentation API
                </a>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={closeEditModal}
                className="flex-1 rounded-xl"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSaveGoogleLocation}
                disabled={saving || !googleLocationId}
                className="flex-1 rounded-xl bg-violet-600 hover:bg-violet-700"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Sauvegarder
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Google Locations Modal */}
      {showLocationsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 animate-fade-in max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                Mes établissements Google
              </h2>
              <button 
                onClick={() => setShowLocationsModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingLocations ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
                <span className="ml-3 text-slate-500">Chargement des établissements...</span>
              </div>
            ) : googleLocations.length > 0 ? (
              <div className="overflow-y-auto flex-1 space-y-3">
                {googleLocations.map((location, index) => (
                  <div 
                    key={index}
                    className="p-4 border border-slate-200 rounded-xl hover:border-violet-300 hover:bg-violet-50 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900">
                          {location.title || location.locationName || "Établissement"}
                        </h4>
                        {location.storefrontAddress && (
                          <p className="text-sm text-slate-500 mt-1">
                            {location.storefrontAddress.addressLines?.[0] || ""}
                            {location.storefrontAddress.locality && `, ${location.storefrontAddress.locality}`}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono text-slate-600 truncate max-w-[250px]">
                            {location.name}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(location.name);
                              toast.success("Location ID copié !");
                            }}
                            className="p-1 text-slate-400 hover:text-violet-600"
                            title="Copier"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {editingOrg && (
                        <Button
                          size="sm"
                          onClick={() => selectLocationForOrg(location)}
                          className="rounded-lg bg-violet-600 hover:bg-violet-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Utiliser
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-2">Aucun établissement trouvé</p>
                <p className="text-sm text-slate-400">
                  Vérifiez que vous êtes bien Manager des établissements clients sur Google Business.
                </p>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={() => setShowLocationsModal(false)}
                className="w-full rounded-xl"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrganizations;
